import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, AfterViewInit, OnDestroy, Output, ViewChild, inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'app-day-slider',
  templateUrl: './day-slider.component.html',
  styleUrls: ['./day-slider.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: []
})
export class DaySliderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() showCalendarIcon = true;
  @Input() disablePastDays = false;
  @Output() dateSelected = new EventEmitter<Date>();
  private platformId = inject(PLATFORM_ID);
  
  @ViewChild('daysContainer') daysContainer!: ElementRef;
  
  days: { date: Date; dayOfMonth: number; dayName: string; isToday: boolean }[] = [];
  @Input() selectedDate: Date = new Date();
  showCalendarPopup = false;
  
  // Touch handling variables
  touchStartX = 0;
  touchEndX = 0;
  
  // Default number of days to show if calculation fails
  defaultDaysToShow = 30;
  
  // Minimum number of days to show
  minDaysToShow = 30;
  
  // Current number of days being displayed
  currentDaysCount = this.defaultDaysToShow;
  
  // Day names in German to match the calendar component
  dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  
  // Month names in German to match the calendar component
  monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];
  
  constructor() {
    // Initialize with today's date
    this.selectedDate = new Date();
    this.selectedDate.setHours(0, 0, 0, 0);
  }
  
  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.generateDays();
      
      // Generate plenty of days initially
      this.addMoreDays();
      this.addMoreDays();
    }
  }
  
  ngAfterViewInit(): void {
    // Calculate days after view is initialized
    setTimeout(() => {
      this.calculateAndUpdateDays();
    });
    

    if (isPlatformBrowser(this.platformId)) {
    // Add resize listener to recalculate days when window is resized
    window.addEventListener('resize', this.onResize.bind(this));
    }
  }
  
  // Clean up the resize listener when component is destroyed
  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
  }
  
  // Handle window resize events
  onResize(): void {
    this.calculateAndUpdateDays();
  }
  
  // Calculate how many days can fit in the container and update if needed
  calculateAndUpdateDays(): void {
    if (this.daysContainer && this.daysContainer.nativeElement) {
      const containerWidth = this.daysContainer.nativeElement.clientWidth;
      const navButtonsWidth = 96; // Approximate width of both navigation buttons (48px each)
      const availableWidth = containerWidth - navButtonsWidth;
      
      // Get the width of a single day item
      // We'll use the first day item as a reference
      const dayItems = this.daysContainer.nativeElement.querySelectorAll('.day-item');
      if (dayItems.length > 0) {
        const dayItemWidth = dayItems[0].offsetWidth;
        const dayItemMargin = 8; // 0.5rem gap between items (8px)
        const dayItemTotalWidth = dayItemWidth + dayItemMargin;
        
        // Calculate how many days can fit
        let daysToShow = Math.floor(availableWidth / dayItemTotalWidth);
        
        // Ensure we show at least the minimum number of days
        daysToShow = Math.max(daysToShow, this.minDaysToShow);
        
        // Only regenerate days if the count has changed
        if (daysToShow !== this.currentDaysCount) {
          this.currentDaysCount = daysToShow;
          this.generateDays();
        }
      }
    }
  }
  
  // Generate more days for scrolling
  generateDays(): void {
    this.days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate days centered around the selected date
    // instead of starting from 3 days before
    const startDate = new Date(this.selectedDate);
    
    // Generate days based on the calculated count or default
    const daysToGenerate = this.currentDaysCount || this.defaultDaysToShow;
    
    for (let i = 0; i < daysToGenerate; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      this.days.push({
        date: currentDate,
        dayOfMonth: currentDate.getDate(),
        dayName: this.dayNames[currentDate.getDay()],
        isToday: currentDate.toDateString() === today.toDateString()
      });
    }
    
    // Scroll to selected date (if it's valid)
    setTimeout(() => {
      // If selected date is before today, update it to today
      const selectedDateTime = this.selectedDate.getTime();
      const todayTime = today.getTime();
      
      if (selectedDateTime < todayTime) {
        this.selectedDate = new Date(today);
        this.dateSelected.emit(this.selectedDate);
      }
      
      this.scrollToSelectedDate();
    }, 100);
  }

  // Updated scrolling method
  scrollDays(direction: 'left' | 'right'): void {
    if (!this.daysContainer?.nativeElement) return;
    
    const container = this.daysContainer.nativeElement;
    const scrollAmount = 200;
    
    // If trying to scroll left, check if we'd go past the beginning
    if (direction === 'left') {
      // Get the first visible day
      const scrollPos = container.scrollLeft;
      const dayWidth = 50; // Approximate width of day column
      const visibleIndex = Math.floor(scrollPos / dayWidth);
      
      // If we're already showing the first few days, don't scroll further left
      if (visibleIndex <= 1) {
        return; // Don't scroll past the beginning
      }
    }
    
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  // Public method that can be called from parent components
  scrollToSelectedDate(): void {
    // Wait for rendering to complete
    setTimeout(() => {
      if (!this.daysContainer?.nativeElement) return;
      
      // Check if selected date is in current view
      const selectedIndex = this.days.findIndex(day => 
        this.isSelected(day.date));
      
      // If the selected date is not in the current view, regenerate days around it
      if (selectedIndex === -1) {
        this.generateDays();
        
        // Allow time for the days to render, then try scrolling again
        setTimeout(() => this.performScrollToSelectedDate(), 50);
        return;
      }
      
      this.performScrollToSelectedDate();
    }, 10);
  }

  // Private implementation of the scrolling logic
  private performScrollToSelectedDate(): void {
    if (!this.daysContainer?.nativeElement) return;
    
    const selectedIndex = this.days.findIndex(day => 
      this.isSelected(day.date));
    
    if (selectedIndex > -1) {
      const container = this.daysContainer.nativeElement;
      const dayElements = container.querySelectorAll('.day-item');
      
      if (dayElements[selectedIndex]) {
        // Calculate scroll position to center the selected date
        const dayWidth = dayElements[selectedIndex].offsetWidth;
        const containerWidth = container.offsetWidth;
        const scrollPosition = dayElements[selectedIndex].offsetLeft - 
          (containerWidth / 2) + (dayWidth / 2);
        
        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  }

  // Track scrolling without changing selection
  onScroll(): void {
    // Use this to detect when we need to add more days
    const container = this.daysContainer?.nativeElement;
    if (!container) return;
    
    // Check if we're nearing the end of the scroll area
    const isNearingEnd = container.scrollLeft + container.clientWidth >= 
      container.scrollWidth - 200;
      
    if (isNearingEnd && !this.isAddingDays) {
      this.addMoreDays();
    }
    
    // If scroll position is close to start, prevent further scrolling left
    if (container.scrollLeft < 10) {
      container.scrollLeft = 0; // Snap to the beginning
    }
  }

  // Flag to prevent multiple simultaneous calls
  private isAddingDays = false;

  // Add more days when scrolling near the end
  addMoreDays(): void {
    if (this.isAddingDays) return;
    
    this.isAddingDays = true;
    
    // Get the last date in the array
    const lastDate = this.days[this.days.length - 1].date;
    const today = new Date();
    
    // Add 10 more days
    for (let i = 1; i <= 10; i++) {
      const newDate = new Date(lastDate);
      newDate.setDate(lastDate.getDate() + i);
      
      this.days.push({
        date: newDate,
        dayOfMonth: newDate.getDate(),
        dayName: this.dayNames[newDate.getDay()],
        isToday: newDate.toDateString() === today.toDateString()
      });
    }
    
    // Allow adding more days again after a small delay
    setTimeout(() => {
      this.isAddingDays = false;
    }, 300);
  }

  // When day is selected, scroll to center it
  selectDay(day: { date: Date }): void {
    // Only allow selection if the date is not in the past when disablePastDays is true
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    console.log('Day selected:', day.date);
    
    if (!this.disablePastDays || day.date >= today) {
      this.selectedDate = new Date(day.date);
      this.dateSelected.emit(this.selectedDate);
      
      // Don't call scrollToSelectedDate() here to avoid jumpy behavior
      // The scroll position should stay where the user clicked
    }
  }
  
  isSelected(date: Date): boolean {
    return date.toDateString() === this.selectedDate.toDateString();
  }
  
  isPastDay(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }
  
  navigateDays(direction: 'prev' | 'next'): void {
    const offset = direction === 'prev' ? -7 : 7;
    this.selectedDate.setDate(this.selectedDate.getDate() + offset);
    this.generateDays();
  }
  
  openCalendar(): void {
    this.showCalendarPopup = true;
  }
  
  closeCalendar(): void {
    this.showCalendarPopup = false;
    // No need to update the day slider here as we're keeping the current selection
  }
  
  closeCalendarOnOverlayClick(event: MouseEvent): void {
    // Only close if clicking directly on the overlay (not on the calendar)
    if ((event.target as HTMLElement).classList.contains('calendar-popup-overlay')) {
      this.closeCalendar();
    }
  }
  
  // Method to handle calendar selection
  handleCalendarSelection(date: Date): void {
    this.selectedDate = new Date(date);
    this.showCalendarPopup = false;
    this.generateDays();
    this.dateSelected.emit(this.selectedDate);
  }
  
  // Touch event handlers for mobile swipe
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.showCalendarPopup) { // Only handle touch events if calendar is not open
      this.touchStartX = event.touches[0].clientX;
    }
  }
  
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.showCalendarPopup) { // Only handle touch events if calendar is not open
      this.touchEndX = event.changedTouches[0].clientX;
      this.handleSwipe();
    }
  }
  
  // Update the preventTouchSelection method
  preventTouchSelection(event: TouchEvent): void {
    // Prevent default to avoid any browser handling
    event.preventDefault();
    
    // We need to determine if this was a tap or a scroll
    const touchEndX = event.changedTouches[0].clientX;
    const touchDelta = touchEndX - this.touchStartX;
    
    // If significant movement (scrolling, not tapping)
    if (Math.abs(touchDelta) > 5) {
      // This was a scroll, not a tap
      event.stopPropagation();
      
      // If scrolling left (swipe right) and we're at beginning, prevent it
      if (touchDelta > 0) {
        const container = this.daysContainer?.nativeElement;
        if (container && container.scrollLeft < 50) {
          // Reset scroll position to prevent bouncing effect
          container.scrollTo({
            left: 0,
            behavior: 'auto'
          });
        }
      }
    }
  }

  // And modify the existing handleSwipe method to not change selection
  handleSwipe(): void {
    const SWIPE_THRESHOLD = 50; // Minimum distance required for a swipe
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (Math.abs(swipeDistance) > SWIPE_THRESHOLD) {
      if (swipeDistance > 0) {
        // Swipe right - go to previous days
        this.scrollDays('left'); // Use scrollDays instead of navigateDays
      } else {
        // Swipe left - go to next days
        this.scrollDays('right'); // Use scrollDays instead of navigateDays
      }
    }
  }
  
  // Format date for display in the header
  formatDate(date: Date): string {
    return `${date.getDate()}. ${this.monthNames[date.getMonth()]} ${date.getFullYear()}`;
  }
  
  getShortMonth(date: Date): string {
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return monthNames[date.getMonth()];
  }
}